// Shared, pure helpers that turn an echo + its contact into the two hero badges:
// "where did this person come from?" (attribution source) and "is this a recurring
// user / was this a repeat echo?" (recurrence).
//
// Data model (see plans/2026-07-09-crm-echo-recurring-and-attribution-badges-plan.md):
//   - Attribution is first-touch, per-CONTACT (set-once on the bot `users` row,
//     projected to the contact by dashboard-api). The echo itself only carries a
//     per-echo `campaign_source` (today: "snap"; agency slugs are the other case).
//   - The contact exposes a PRE-COMPUTED normalized `acquisition_channel`
//     (∈ website|ctwa|organic|snap) — prefer it over parsing `acquisition_source`.
//   - Recurrence lives on the contact: `is_recurring` (≥2 delivered echoes), plus
//     the raw counts / `lifecycle_stage` / `first_completed_echo_at` fallbacks.

import type { Contact, EchoflicksVideoSession } from "../types";
import { isStudioFilm } from "./studioFilms";

export type AttributionChannel =
  | "snap"
  | "ctwa"
  | "website"
  | "link"
  | "launchpad"
  | "my_web"
  | "referral"
  | "organic"
  | "studio"
  | "other";

export type EchoAttribution = {
  channel: AttributionChannel;
  /** Human-readable label for the badge (no emoji). */
  label: string;
  /** Optional leading emoji (only Snap today). */
  emoji?: string;
  token: string | null;
  adId: string | null;
  headline: string | null;
  booth: string | null;
  at: string | null;
  /** One-line operator detail for the badge tooltip/title, or null. */
  detail: string | null;
};

export type EchoRecurrence = {
  /** The contact is a repeat user (≥2 delivered echoes). Describes the PERSON. */
  isRecurringUser: boolean;
  /** Total delivered echoes for the contact, if known. */
  deliveredCount: number | null;
  /**
   * Whether THIS echo came after the contact's first delivered echo. Describes
   * THIS echo (not the person). null when it can't be derived (missing dates).
   */
  isThisEchoRepeat: boolean | null;
  firstEchoAt: string | null;
};

export type EchoReferralAttribution = {
  code: string;
  referrerName: string | null;
  referrerContactId: number | string | null;
  redeemedAt: string;
};

/**
 * `echo_origin` value the bot stamps on a synthetic-but-real Echo launched by the
 * test-echo autoqueue (debug menu / internal API / CRM Labs). Everything else —
 * every customer Echo — carries no `echo_origin` at all.
 */
export const TEST_ECHO_ORIGIN = "test_autoqueue";

/** True for an Echo launched by the test-echo autoqueue. */
export function isTestEcho(
  echo?: Pick<EchoflicksVideoSession, "echo_origin"> | null,
): boolean {
  return nonEmptyString(echo?.echo_origin) === TEST_ECHO_ORIGIN;
}

/**
 * Operator detail for the 🧪 badge tooltip: "Pack: eevee · ffr sheet refs run 3".
 * Null when the projection carries neither, so the badge renders without a title.
 */
export function testEchoTooltip(
  echo?: Pick<EchoflicksVideoSession, "test_pack_id" | "test_label"> | null,
): string | null {
  const parts: string[] = [];
  const packId = nonEmptyString(echo?.test_pack_id);
  const label = nonEmptyString(echo?.test_label);
  if (packId) parts.push(`Pack: ${packId}`);
  if (label) parts.push(label);
  return parts.length ? parts.join(" · ") : null;
}

const CHANNEL_LABELS: Record<AttributionChannel, string> = {
  snap: "Snap",
  ctwa: "Meta ad → WhatsApp",
  website: "Website",
  link: "Tracking link",
  launchpad: "Launchpad",
  // Web Create (MYC-015): first Echo made on my.echoflicks.com, not WhatsApp.
  my_web: "my.echoflicks.com",
  referral: "Invited by referral",
  organic: "Organic / shared",
  studio: "Studio film",
  other: "Campaign",
};

const CHANNEL_EMOJI: Partial<Record<AttributionChannel, string>> = {
  snap: "📸",
  launchpad: "🚀",
  my_web: "🌐",
};

const KNOWN_CHANNELS: AttributionChannel[] = [
  "snap",
  "ctwa",
  "website",
  "link",
  "launchpad",
  "my_web",
  "referral",
  "organic",
  "other",
];

export function channelLabel(channel: AttributionChannel): string {
  return CHANNEL_LABELS[channel] ?? CHANNEL_LABELS.organic;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function isKnownChannel(value: string): value is AttributionChannel {
  return (KNOWN_CHANNELS as string[]).includes(value);
}

/** Turn "eu-spring-25" or "acme-agency" into "Eu spring 25" for a label. */
function prettifySlug(slug: string): string {
  const spaced = slug.replace(/[-_]+/g, " ").trim();
  if (!spaced) return slug;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Classify a raw first-touch `acquisition_source` string into a normalized
 * channel + (for website) its campaign token. Exported so the per-contact
 * Acquisition block can share the exact same classification.
 */
export function channelFromRawSource(raw?: string | null): {
  channel: AttributionChannel;
  token: string | null;
} {
  const value = nonEmptyString(raw);
  if (!value) return { channel: "organic", token: null };
  if (value === "organic") return { channel: "organic", token: null };
  if (value === "snap") return { channel: "snap", token: null };
  if (value === "my_web") return { channel: "my_web", token: null };
  if (value === "meta_ctwa") return { channel: "ctwa", token: null };
  if (value === "referral" || value === "gift_referral") {
    return { channel: "referral", token: null };
  }
  if (value === "launchpad" || value.startsWith("launchpad:")) {
    return {
      channel: "launchpad",
      token: value.startsWith("launchpad:")
        ? value.slice("launchpad:".length) || null
        : null,
    };
  }
  if (value.startsWith("link:")) {
    return {
      channel: "link",
      token: value.slice("link:".length).toUpperCase() || null,
    };
  }
  if (value.startsWith("website:")) {
    const rawToken = value.slice("website:".length) || null;
    const token =
      rawToken === "web"
        ? "organic"
        : rawToken === "lead_campaign_jul26"
          ? "lead_campaign_july26"
          : rawToken;
    return {
      channel: "website",
      token,
    };
  }
  return { channel: "other", token: null };
}

function detailFields(contact?: Partial<Contact> | null) {
  const detail = (contact?.acquisition_detail ?? {}) as Record<string, unknown>;
  return {
    headline: nonEmptyString(detail.headline),
    adId: nonEmptyString(detail.source_id),
    token: nonEmptyString(detail.token),
    booth: nonEmptyString(detail.booth),
    campaign: nonEmptyString(detail.utm_campaign),
    at: nonEmptyString(contact?.acquisition_at),
  };
}

function detailLine(
  channel: AttributionChannel,
  parts: {
    headline: string | null;
    adId: string | null;
    token: string | null;
    booth: string | null;
    campaign: string | null;
  },
): string | null {
  switch (channel) {
    case "ctwa": {
      const bits: string[] = [];
      if (parts.headline) bits.push(`“${parts.headline}”`);
      if (parts.adId) bits.push(`Ad ${parts.adId}`);
      return bits.length ? bits.join(" · ") : null;
    }
    case "website":
      return parts.token ? `Campaign: ${parts.token}` : null;
    case "link":
      return parts.token ? `Tracking code: ${parts.token}` : null;
    case "snap": {
      const bits: string[] = [];
      if (parts.booth) bits.push(`Booth: ${parts.booth}`);
      if (parts.token) bits.push(parts.token);
      return bits.length ? bits.join(" · ") : null;
    }
    case "launchpad":
      // The launchpad acquisition_detail carries the Meta UTMs; the campaign
      // is the operator-meaningful bit (the EFL token shows on the contact).
      return parts.campaign ? `Campaign: ${parts.campaign}` : null;
    case "referral":
      return parts.token ? `Gift code: ${parts.token}` : null;
    case "other":
      return parts.token ? `Campaign: ${parts.token}` : null;
    case "organic":
    default:
      return null;
  }
}

/**
 * Resolve the attribution source for an echo. Precedence (most-specific first):
 *  0. a Studio film → "studio" (made in an agency's studio; its `studio-`
 *     phantom contact has no acquisition, so it used to read "Organic / shared")
 *  1. echo.campaign_source === "snap" → snap (authoritative for the Snap campaign)
 *  2. echo.campaign_source is another slug → "other" (agency precedent), NOT ctwa
 *  3. new-user gift redemption → referral (authoritative inviter relationship)
 *  4. contact.acquisition_channel (pre-computed by the dashboard-api) — preferred
 *  5. parse contact.acquisition_source
 *  6. organic (no tracked campaign — e.g. someone shared a phone number)
 */
export function resolveEchoAttribution(
  echo?:
    | (Pick<EchoflicksVideoSession, "campaign_source" | "campaign_token"> &
        Partial<
          Pick<
            EchoflicksVideoSession,
            | "studio_agency_id"
            | "studio_echo_id"
            | "whatsapp_user_id"
            | "contact_id"
          >
        >)
    | null,
  contact?: Partial<Contact> | null,
  referral?: EchoReferralAttribution | null,
  studio?: { agencyName?: string | null } | null,
): EchoAttribution {
  // 0: B6.3 — the agency's name when the admin-only directory knows it.
  if (isStudioFilm(echo)) {
    const agencyName = nonEmptyString(studio?.agencyName);
    return {
      channel: "studio",
      label: agencyName ? `Studio · ${agencyName}` : channelLabel("studio"),
      token: null,
      adId: null,
      headline: null,
      booth: null,
      at: null,
      detail: agencyName
        ? `Made in ${agencyName}'s studio (agency portal)`
        : "Made in an agency's studio (agency portal)",
    };
  }

  const fields = detailFields(contact);

  const buildFor = (
    channel: AttributionChannel,
    overrides?: { label?: string; token?: string | null },
  ): EchoAttribution => {
    const token = overrides?.token ?? fields.token;
    return {
      channel,
      label: overrides?.label ?? channelLabel(channel),
      emoji: CHANNEL_EMOJI[channel],
      token,
      adId: fields.adId,
      headline: fields.headline,
      booth: fields.booth,
      at: fields.at,
      detail: detailLine(channel, { ...fields, token }),
    };
  };

  // 1-2: per-echo campaign_source wins (it is the most specific to THIS echo).
  const campaign = nonEmptyString(echo?.campaign_source);
  if (campaign === "snap") return buildFor("snap");
  if (campaign === "launchpad") {
    // Launchpad echoes carry the EFL token in campaign_token.
    const token = nonEmptyString(echo?.campaign_token) ?? fields.token;
    return buildFor("launchpad", { token });
  }
  if (campaign === "link") {
    const token =
      nonEmptyString(echo?.campaign_token)?.toUpperCase() ?? fields.token;
    return buildFor("link", { token });
  }
  if (campaign?.startsWith("link:")) {
    return buildFor("link", {
      token: campaign.slice("link:".length).toUpperCase() || fields.token,
    });
  }
  if (campaign) {
    return buildFor("other", {
      label: prettifySlug(campaign),
      token: campaign,
    });
  }

  // A new user's redeemed gift code is authoritative first-touch evidence for
  // the inviter -> friend relationship. Existing-user redemptions stay visible
  // in Referrals but do not replace the user's acquisition source. Keep raw
  // phone numbers out of operator copy; name and code are enough for auditing.
  if (referral) {
    const invitedBy = referral.referrerName?.trim();
    const codeDetail = `Gift code: ${referral.code}`;
    return {
      ...buildFor("referral", { token: referral.code }),
      at: referral.redeemedAt,
      detail: invitedBy
        ? `${codeDetail} · Invited by ${invitedBy}`
        : codeDetail,
    };
  }

  // Contact acquisition is the fallback when there is no referral ledger row.
  const channelRaw = nonEmptyString(contact?.acquisition_channel);
  if (channelRaw && isKnownChannel(channelRaw)) {
    const token =
      channelRaw === "website" || channelRaw === "link"
        ? (fields.token ??
          channelFromRawSource(contact?.acquisition_source).token)
        : fields.token;
    return buildFor(channelRaw, { token });
  }

  // 4: parse the raw first-touch source.
  const parsed = channelFromRawSource(contact?.acquisition_source);
  return buildFor(parsed.channel, {
    token: parsed.token ?? fields.token,
  });
}

// Echoes are minutes apart in practice; this tolerance only absorbs clock/precision
// skew between an echo's own completion time and the contact's derived
// first_completed_echo_at, so the debut echo never mis-reads as a repeat.
const FIRST_ECHO_TOLERANCE_MS = 60_000;

/** Resolve whether the contact is a recurring user and whether THIS echo is a repeat. */
export function resolveEchoRecurrence(
  echo?:
    | (Pick<
        EchoflicksVideoSession,
        "completed_at" | "display_date" | "echo_origin"
      > &
        Partial<
          Pick<
            EchoflicksVideoSession,
            | "studio_agency_id"
            | "studio_echo_id"
            | "whatsapp_user_id"
            | "contact_id"
          >
        >)
    | null,
  contact?: Partial<Contact> | null,
): EchoRecurrence {
  // A test Echo is synthetic traffic an admin queued to exercise the pipeline; it
  // is not a customer echo and must never move recurrence numbers (plan
  // 2026-09-02 §3.6 / G19). Return the neutral shape so it neither claims a
  // position in the user's history ("1st echo" / "Repeat echo") nor contributes a
  // delivered count — the 🧪 Test Echo badge is what the operator sees instead.
  // A Studio film has no WhatsApp user either: its `studio-` phantom contact is
  // shared by the film's re-cuts, so its counts would read as a "Recurring
  // user" (B6.3).
  if (isTestEcho(echo) || isStudioFilm(echo)) {
    return {
      isRecurringUser: false,
      deliveredCount: null,
      isThisEchoRepeat: null,
      firstEchoAt: nonEmptyString(contact?.first_completed_echo_at),
    };
  }

  const deliveredCount =
    numberOrNull(contact?.completed_echo_count) ??
    numberOrNull(contact?.video_count);

  const isRecurringUser =
    contact?.is_recurring === true ||
    (deliveredCount != null && deliveredCount >= 2) ||
    contact?.lifecycle_stage === "repeat";

  const firstEchoAt = nonEmptyString(contact?.first_completed_echo_at);
  const echoAt =
    nonEmptyString(echo?.completed_at) ?? nonEmptyString(echo?.display_date);

  let isThisEchoRepeat: boolean | null = null;
  if (echoAt && firstEchoAt) {
    const echoTime = new Date(echoAt).getTime();
    const firstTime = new Date(firstEchoAt).getTime();
    if (Number.isFinite(echoTime) && Number.isFinite(firstTime)) {
      isThisEchoRepeat = echoTime > firstTime + FIRST_ECHO_TOLERANCE_MS;
    }
  }

  return {
    isRecurringUser,
    deliveredCount,
    isThisEchoRepeat,
    firstEchoAt,
  };
}
